import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { canSplitStatus } from "@redotech/redo-fulfillment-group-common/fulfillment-group-status";
import { groupFulfillmentOrderLineItems } from "@redotech/redo-fulfillment-group-common/fulfillment-order-line-item";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { Flex } from "@redotech/redo-web/flex";
import { memo, useContext, useMemo } from "react";
import { CurrentInventoryButton } from "../../common/current-inventory-button";
import { ItemsTable } from "../../common/items-table";
import { SplitGroupButton } from "../../common/split-group-button";
import { useFulfillmentGroupPermissions } from "../../utils/use-assigned-fulfillment-service";

export const FulfillmentItemsCard = memo(function FulfillmentItemsCard({
  fulfillmentGroups,
  setRows,
}: {
  fulfillmentGroups: readonly FulfillmentGroupData[];
  setRows?: (value: React.SetStateAction<FulfillmentGroupData[]>) => void;
}) {
  const team = useContext(TeamContext);
  const lineItems = useMemo(() => {
    return groupFulfillmentOrderLineItems(
      fulfillmentGroups.flatMap((group) => group.items),
      {
        degroupByCustomAttributes:
          team?.settings.outboundLabels?.degroupItemsByCustomAttributes,
        preferRedoProductId: true,
      },
    );
  }, [
    fulfillmentGroups,
    team?.settings.outboundLabels?.degroupItemsByCustomAttributes,
  ]);

  return <ItemsTable items={lineItems} showBin team={team} />;
});

export const FulfillmentItemsCardHeaderActions = memo(
  function FulfillmentItemsCardHeaderActions({
    fulfillmentGroups,
    setRows,
  }: {
    fulfillmentGroups: readonly FulfillmentGroupData[];
    setRows?: (value: React.SetStateAction<FulfillmentGroupData[]>) => void;
  }) {
    const { permissionsToEditEveryGroup } =
      useFulfillmentGroupPermissions(fulfillmentGroups);

    const lineItems = useMemo(() => {
      return groupFulfillmentOrderLineItems(
        fulfillmentGroups.flatMap((group) => group.items),
        { degroupByCustomAttributes: false, preferRedoProductId: true },
      );
    }, [fulfillmentGroups]);

    const lineItemsQuantity = useMemo(() => {
      return lineItems.reduce((acc, item) => acc + item.quantity, 0);
    }, [lineItems]);

    const canSplit = useMemo(() => {
      return (
        fulfillmentGroups.length === 1 &&
        lineItemsQuantity > 1 &&
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        canSplitStatus(fulfillmentGroups[0].status)
      );
    }, [fulfillmentGroups, lineItemsQuantity]);

    const firstGroup = fulfillmentGroups[0];

    return (
      <Flex align="center" gap="sm">
        <CurrentInventoryButton fulfillmentGroups={fulfillmentGroups} />
        {canSplit && firstGroup && (
          <SplitGroupButton
            disabled={!permissionsToEditEveryGroup}
            fulfillmentGroup={firstGroup}
            isSlideout
            setRows={setRows}
            size="sm"
            triggerReload={async () => {}}
          />
        )}
      </Flex>
    );
  },
);
